// @ts-strict-ignore
/* globals mixpanel */

import {objToQueryString} from 'common/util';
import {apiClient} from 'common/util/api-client';
import {reporter} from 'common/report/error-reporting';
import {FetchOptions} from 'common/util/shared-client';
import {attachStructuredPayload, parseStructuredApiErrorPayload} from 'common/util/structured-api-error';

export function appApiUrl(
  endpoint,
  {projectId = null, workspaceId = null}: {projectId?: number | string; workspaceId?: number | string} = {},
) {
  if (projectId || workspaceId) {
    workspaceId = Number.parseInt(workspaceId as string) || null;
    const projectOrWorkspace = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
    endpoint = `${projectOrWorkspace}/${endpoint}`;
  }
  return `/api/app/${endpoint}`;
}

export function queryApiUrl(endpoint) {
  return `/api/query/${endpoint}`;
}

/*
 * Helper function to parse json from successful responses and throw Error
 * if error property is present.
 * @param {Promise} fetchPromise - fetch() request to a JSON endpoint
 * @returns {object} deserialized response if successful
 */
export async function handleJsonRequest(fetchPromise: Promise<Response>) {
  let response = null;

  try {
    response = await fetchPromise;
  } catch {
    throw new Error(`Please contact support if issue persists.`);
  }

  let body = null;

  try {
    body = await response.json();
  } catch {
    // `fetch` follows redirects by default so an expired session
    // gets silently bounced to the login page, which returns HTML — not JSON
    // despite a 200 response.
    if (response.status === 200 && response.redirected) {
      throw new Error(`Your session has expired. Please refresh the page and try again.`);
    }
    throw new Error(`${response.status}: ${response.statusText}. Please contact support if issue persists.`);
  }

  if (body.error) {
    reporter.error(new Error(body.error));
    // Attach `type` (server-categorized error code, e.g. `payment_in_progress`)
    // and `status` to the thrown Error so callers can branch on known cases
    // and render a localized message rather than a generic failure toast.
    const err = new Error(body.error) as Error & {type?: string; status?: number};
    if (body.type) {
      err.type = body.type;
    }
    err.status = response.status;
    throw attachStructuredPayload(err, parseStructuredApiErrorPayload(body));
  }

  return body;
}

/**
 * Helper function to parse the error to see if it is a data classification error
 * @returns true if the error is a data classification error
 */
export function isDataClassificationError(error: string): boolean {
  // TODO(zihe): Currently this is a workaround to check if it is a data classification error,
  // will need to refactor by introducing errorType to properly define a data classification error
  return !!error && typeof error === `string` && !!/marked classified/i.exec(error);
}

export function trackDataClassificationError(report: string) {
  try {
    mixpanel.track(`[Lexicon] Accessed report with no sensitive data permission`, {'Report name': report});
  } catch (err) {
    reporter.error(err);
  }
}

export function trackMixpanelTips(tipIndex: number, duration?: number) {
  if (typeof mixpanel !== `undefined`) {
    const isHideEvent = !!duration;
    const eventName = `[Mixpanel Tips] - ` + (isHideEvent ? `Hide` : `Show`);
    const properties: {tipIndex: number; 'Tip Shown Duration (ms)'?: number} = {tipIndex};

    if (isHideEvent) {
      properties[`Tip Shown Duration (ms)`] = duration;
    }

    mixpanel.track(eventName, properties);
  }
}

export function isUnauthorizedWorkspaceError({status, error}: {status?: number; error?: string} = {}): boolean {
  return Boolean(status) && Boolean(error) && status === 403 && /User is not a member of this workspace/.test(error);
}

/**
 * Helper function to parse the error to see if it is a missing datagroup error
 * @returns true if the error is a missing mapped datagroup.
 */
export function isMissingDataGroupError(error: string): boolean {
  return !!error && typeof error === `string` && error === `Missing Datagroup`;
}

export function trackMissingDatagroupError(report: string) {
  try {
    mixpanel.track(`[Lexicon] Mapped property datagroup missing`, {'Report name': report});
  } catch (err) {
    reporter.error(`tracking error`, err);
  }
}

/**
 * Helper function to parse the error to see if it is an unsupported action error
 * @returns true if the error is due to a request that is unsupported/requires
 * further user action to succeed
 */
export function isUnsupportedActionError(status: number): boolean {
  return status === 422 || status === 412;
}

export function isArgumentListError(error: string) {
  return !!error && typeof error === `string` && error.includes(`argument list too long`);
}

export function isCardinalityLimitError(error: string) {
  return !!error && typeof error === `string` && error.includes(`cardinality limit exceeded`);
}

export function isBadRequestError(status: number): boolean {
  return status === 400;
}

export function isNotFoundError(status: number): boolean {
  return status === 404;
}

export function isTimeoutError(status: number): boolean {
  return status === 504;
}

export function trackUnsupportedActionError(report: string, error: string) {
  try {
    mixpanel.track(`[Lexicon] UnsupportedActionError`, {'Report name': report, error});
  } catch (err) {
    reporter.error(`tracking error`, err);
  }
}

export function isUserVisibleError(error: string): boolean {
  return (
    isUserVisibleParamsError(error) ||
    isUserVisiblePreconditionFailedError(error) ||
    isUserVisibleNotFoundError(error) ||
    isUserVisibleNonRetryableApiError(error)
  );
}

export function trimUserVisibleError(error: string): string {
  if (isUserVisibleParamsError(error)) {
    return trimUserVisibleParamsError(error);
  } else if (isUserVisiblePreconditionFailedError(error)) {
    return trimUserVisiblePreconditionFailedError(error);
  } else if (isUserVisibleNotFoundError(error)) {
    return trimUserVisibleNotFoundError(error);
  } else if (isUserVisibleNonRetryableApiError(error)) {
    return trimUserVisibleNonRetryableApiError(error);
  }
  return error;
}

function isUserVisibleParamsError(error: string): boolean {
  return !!error && typeof error === `string` && error.startsWith(`UserVisibleParamsError: `);
}

function trimUserVisibleParamsError(error: string): string {
  if (isUserVisibleParamsError(error)) {
    return error.slice(`UserVisibleParamsError: `.length);
  }
  return error;
}

function isUserVisiblePreconditionFailedError(error: string): boolean {
  return !!error && typeof error === `string` && error.startsWith(`UserVisiblePreconditionFailedError: `);
}

function trimUserVisiblePreconditionFailedError(error: string): string {
  if (isUserVisiblePreconditionFailedError(error)) {
    return error.slice(`UserVisiblePreconditionFailedError: `.length);
  }
  return error;
}

function isUserVisibleNotFoundError(error: string): boolean {
  return !!error && typeof error === `string` && error.startsWith(`UserVisibleNotFoundError: `);
}

function trimUserVisibleNotFoundError(error: string): string {
  if (isUserVisibleNotFoundError(error)) {
    return error.slice(`UserVisibleNotFoundError: `.length);
  }
  return error;
}

function isUserVisibleNonRetryableApiError(error: string): boolean {
  return !!error && typeof error === `string` && error.startsWith(`UserVisibleNonRetryableApiError: `);
}

function trimUserVisibleNonRetryableApiError(error: string): string {
  if (isUserVisibleNonRetryableApiError(error)) {
    return error.slice(`UserVisibleNonRetryableApiError: `.length);
  }
  return error;
}

export function isCohortDateRangeError(error: string): boolean {
  return !!error && typeof error === `string` && error.includes(`Cohort definition dates`);
}

export function isInvalidFormulasError(error: string): boolean {
  return !!error && typeof error === `string` && error.includes(`formulas are invalid`);
}

export function isUnsupportedAttributionError(error: string): boolean {
  return !!error && typeof error === `string` && error.includes(`Attribution models`);
}

export function isHPCListNotSupported(error: string): boolean {
  return !!error && typeof error === `string` && error.includes(`List property`);
}

export function isMultipleHPCWithList(error: string): boolean {
  return !!error && typeof error === `string` && error.includes(`Can only have only one HPC List`);
}

/*
 * Hit legacy Django API endpoints using session auth
 */
export async function djangoFetch(
  url: string,
  data: Obj = {},
  options: RequestInit & {abortSignal?: AbortSignal} = {},
  queryStringParams: Obj = {},
) {
  const queryString = objToQueryString(queryStringParams);
  const newUrl = url + (queryString ? `?${queryString}` : ``);

  const payload: RequestInit = {
    body: options.method === `GET` ? null : objToQueryString(data),
    credentials: `include`,
    method: options.method || `POST`,
    headers: {
      'Content-Type': `application/x-www-form-urlencoded`,
    },
    signal: options.abortSignal,
    ...options,
  };

  if (options.method === `GET`) {
    delete payload.body;
  }

  return fetch(newUrl, payload);
}

/*
 * Wraps djangoFetch with JSON parsing of response and error-handling
 */
export async function djangoFetchJson(
  url: string,
  data: Obj = {},
  options: RequestInit & {csrfToken?: string; abortSignal?: AbortSignal} = {},
  queryStringParams: Obj = {},
) {
  return handleJsonRequest(djangoFetch(url, data, options, queryStringParams));
}

/**
 * Wraps common apiClient which wraps http fetch api
 * Handles error handling and response parsing
 * @param url - REST api endpoint url
 * @param fetchParams - Fetch params
 * @param queryParams - a list of optional query params
 */
export async function appApiFetchJson<ResultType = any>(
  url: string,
  fetchParams: FetchOptions = {},
  queryParams: Record<string, any> = {},
): Promise<ResultType> {
  // apiClient's fetch which will automatically fetch working bearer token if expired or missing
  const queryString = objToQueryString(queryParams);
  url += queryString ? `?${queryString}` : ``;
  // TODO: make all appApiFetchJson callers use apiClient.get|post|put|patch|delete instead
  // the helper methods correctly parse errors and unwrap the ResultsType
  // eslint-disable-next-line local-rules/deprecated-member-access
  return (await handleJsonRequest(apiClient.fetch(url, fetchParams))).results;
}
